/**
 * EmptyState — 統一的空狀態 / 錯誤狀態 / 占位（v3 "Quiet Refinement"）
 * ──────────────────────────────────────────────
 * 升級亮點：
 *   - 圖標：大型幾何 outlined（lucide，strokeWidth 1.2），尺寸 48-64px，不可愛不花哨
 *   - 標題：Onest display，weight 600，stone 暖黑
 *   - 說明文字：stone-500 弱化，max-width 限制行寬
 *   - 操作按鈕：可選傳入，右對齊或居中均可
 *   - 不使用 antd Empty（視覺過重）
 */
import type { ReactNode } from 'react';
import { Inbox, AlertCircle, PackageOpen } from 'lucide-react';
import { color, font, leading, space, text, tracking, weight } from '@/styles/tokens';

type Variant = 'empty' | 'error' | 'search';

export interface EmptyStateProps {
  /** 變體：empty（無數據）/ error（加載失敗）/ search（搜索無結果） */
  variant?: Variant;
  /** 自定義圖標（覆蓋默認，傳 lucide ReactNode） */
  icon?: ReactNode;
  /** 標題 */
  title: ReactNode;
  /** 說明（支持富文本） */
  description?: ReactNode;
  /** 操作按鈕（自由傳入） */
  action?: ReactNode;
  /** 尺寸：sm（卡片內）/ md（區塊）/ lg（全頁） */
  size?: 'sm' | 'md' | 'lg';
}

/** 每個 variant 的默認圖標 + 色彩 */
const VARIANT_CONFIG: Record<Variant, { Icon: typeof Inbox; iconColor: string; iconBg: string }> = {
  empty: { Icon: Inbox, iconColor: color.text.muted, iconBg: color.surface.sunken },
  search: { Icon: PackageOpen, iconColor: color.text.muted, iconBg: color.surface.sunken },
  error: { Icon: AlertCircle, iconColor: color.state.danger, iconBg: color.state.dangerSoft },
};

export default function EmptyState({
  variant = 'empty',
  icon,
  title,
  description,
  action,
  size = 'md',
}: EmptyStateProps) {
  const conf = VARIANT_CONFIG[variant];

  /* 尺寸映射 */
  const dim = {
    sm: { iconSize: 28, iconBox: 52, titleSize: text.md, pad: space[6] },
    md: { iconSize: 40, iconBox: 68, titleSize: text.lg, pad: space[10] },
    lg: { iconSize: 52, iconBox: 88, titleSize: text.xl, pad: space[16] },
  }[size];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: dim.pad,
        gap: space[4],
      }}
    >
      {/* 幾何圖標（圓形底座 + outlined icon） */}
      <div
        style={{
          width: dim.iconBox,
          height: dim.iconBox,
          borderRadius: '50%',
          background: conf.iconBg,
          border: `1px solid ${color.border.subtle}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: conf.iconColor,
          flexShrink: 0,
        }}
      >
        {icon ?? <conf.Icon size={dim.iconSize} strokeWidth={1.2} />}
      </div>

      {/* 文字區 */}
      <div>
        <div
          style={{
            fontFamily: font.display,
            fontSize: dim.titleSize,
            fontWeight: weight.semibold,
            color: color.text.primary,
            letterSpacing: tracking.tight,
            lineHeight: leading.tight,
            marginBottom: description ? 8 : 0,
          }}
        >
          {title}
        </div>
        {description && (
          <div
            style={{
              fontSize: text.sm,
              color: color.text.tertiary,
              lineHeight: leading.normal,
              maxWidth: 360,
            }}
          >
            {description}
          </div>
        )}
      </div>

      {/* CTA 按鈕 */}
      {action && <div style={{ marginTop: space[2] }}>{action}</div>}
    </div>
  );
}
